import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Zap, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import RollerLabsLogo from "@/components/RollerLabsLogo";

const TOTAL_STEPS = 5;

const GetStarted = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    venueName: "",
    venueWebsite: "",
  });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="mb-8">
        <RollerLabsLogo size="md" />
      </div>

      {/* Progress */}
      <div className="mb-8 flex items-center gap-2">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-colors ${
              step > s ? "bg-accent text-accent-foreground" : step === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              {step > s ? <Check className="h-4 w-4" /> : s}
            </div>
            {s < TOTAL_STEPS && <div className={`h-0.5 w-8 ${step > s ? "bg-accent" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Pricing */}
        {step === 1 && (
          <StepWrapper stepKey="plan">
            <div className="rounded-2xl border-2 border-primary bg-card p-8 text-center shadow-lg">
              <p className="text-sm font-semibold uppercase tracking-wider text-primary">Leaderboards</p>
              <p className="mt-4 text-4xl font-extrabold">$40<span className="text-lg font-normal text-muted-foreground">/mo</span></p>
              <p className="mt-1 text-sm text-muted-foreground">Per venue · 7-day free trial · Cancel anytime</p>
              <ul className="mt-6 space-y-2 text-left text-sm">
                {["7-day free trial — no card required", "Unlimited campaigns", "TV + mobile display", "All templates", "Reporting & CSV export"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" />{f}</li>
                ))}
              </ul>
              <Button className="mt-8 w-full" size="lg" onClick={() => setStep(2)}>
                Start free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </StepWrapper>
        )}

        {/* Step 2: Create Account */}
        {step === 2 && (
          <StepWrapper stepKey="signup">
            <div className="rounded-2xl border bg-card p-8 shadow-lg">
              <h2 className="text-center text-xl font-bold">Create your account</h2>
              <p className="mt-1 text-center text-sm text-muted-foreground">
                Get started with your 7-day free trial.
              </p>

              {/* Google Sign-In */}
              <Button
                variant="outline"
                className="mt-6 w-full gap-2"
                size="lg"
                onClick={() => setStep(3)}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>

              <div className="my-6 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">or</span>
                <Separator className="flex-1" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@yourvenue.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <Button
                className="mt-6 w-full"
                size="lg"
                disabled={!formData.email.trim()}
                onClick={() => setStep(3)}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <button onClick={() => setStep(1)} className="mt-4 block w-full text-center text-sm text-muted-foreground hover:underline">
                Back
              </button>
            </div>
          </StepWrapper>
        )}

        {/* Step 3: Venue Website */}
        {step === 3 && (
          <StepWrapper stepKey="venue">
            <div className="rounded-2xl border bg-card p-8 shadow-lg">
              <h2 className="text-center text-xl font-bold">What's your venue website?</h2>
              <p className="mt-1 text-center text-sm text-muted-foreground">
                We'll use this to personalise your leaderboard experience.
              </p>
              <div className="mt-6 space-y-1.5">
                <Label htmlFor="venueWebsite">Venue website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="venueWebsite"
                    type="url"
                    placeholder="https://yourvenue.com"
                    value={formData.venueWebsite}
                    onChange={(e) => setFormData({ ...formData, venueWebsite: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
              <Button
                className="mt-6 w-full"
                size="lg"
                disabled={!formData.venueWebsite.trim()}
                onClick={() => setStep(4)}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button onClick={() => setStep(2)} className="mt-4 block w-full text-center text-sm text-muted-foreground hover:underline">
                Back
              </button>
            </div>
          </StepWrapper>
        )}

        {/* Step 4: Connect ROLLER */}
        {step === 4 && (
          <StepWrapper stepKey="connect">
            <div className="rounded-2xl border bg-card p-8 text-center shadow-lg">
              <div className="mx-auto mb-4 flex items-center justify-center gap-2">
                <svg viewBox="0 0 24 24" className="h-8 w-8 text-primary" fill="currentColor">
                  <path d="M8 4l8 8-8 8" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-2xl font-extrabold tracking-tight text-foreground">ROLLER</span>
              </div>
              <h2 className="text-xl font-bold">Connect your ROLLER account</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Connect to your <span className="font-semibold text-foreground">{formData.venueWebsite || "venue"}</span> ROLLER account to sync Products, Sales, and Staff data.
              </p>
              <Button className="mt-8 w-full" size="lg" onClick={() => setStep(5)}>
                <Zap className="mr-2 h-4 w-4" /> Grant Access to ROLLER
              </Button>
              <button onClick={() => setStep(3)} className="mt-3 text-sm text-muted-foreground hover:underline">Back</button>
            </div>
          </StepWrapper>
        )}

        {/* Step 5: OAuth Consent */}
        {step === 5 && (
          <StepWrapper stepKey="oauth">
            <OAuthConsent onAllow={() => navigate("/app/welcome")} onCancel={() => setStep(4)} />
          </StepWrapper>
        )}
      </AnimatePresence>
    </div>
  );
};

const StepWrapper = ({ stepKey, children }: { stepKey: string; children: React.ReactNode }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, x: 50 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -50 }}
    className="w-full max-w-md"
  >
    {children}
  </motion.div>
);

const OAuthConsent = ({ onAllow, onCancel }: { onAllow: () => void; onCancel: () => void }) => {
  const [connecting, setConnecting] = useState(false);

  const handleAllow = () => {
    setConnecting(true);
    setTimeout(onAllow, 1500);
  };

  return (
    <div className="rounded-2xl border bg-card p-8 shadow-lg">
      <div className="flex items-center justify-center gap-3 border-b pb-6">
        <RollerLabsLogo size="sm" />
        <span className="text-muted-foreground">→</span>
        <span className="text-sm font-semibold">Leaderboards</span>
      </div>
      <h3 className="mt-6 text-center text-lg font-bold">Authorize Access</h3>
      <p className="mt-1 text-center text-sm text-muted-foreground">
        ROLLER Labs — Leaderboards is requesting the following permissions:
      </p>
      <div className="mt-6 space-y-3">
        {[
          { perm: "Read Products", desc: "View your product catalogue" },
          { perm: "Read Sales / Orders", desc: "View POS transaction data" },
          { perm: "Read Staff / Users", desc: "View POS operator profiles" },
        ].map((p) => (
          <div key={p.perm} className="flex items-start gap-3 rounded-lg bg-secondary p-3">
            <Check className="mt-0.5 h-4 w-4 text-accent" />
            <div>
              <p className="text-sm font-medium">{p.perm}</p>
              <p className="text-xs text-muted-foreground">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={connecting}>Cancel</Button>
        <Button className="flex-1" onClick={handleAllow} disabled={connecting}>
          {connecting ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="mr-2 h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent"
              />
              Connecting…
            </>
          ) : (
            "Allow"
          )}
        </Button>
      </div>
    </div>
  );
};

export default GetStarted;
